<template>
  <v-dialog
    :model-value="show.persona"
    content-class="persona-modal"
    width="570px"
    max-width="100%"
    height="484px"
    @update:model-value="updateModal"
  >
    <v-card width="570px" max-width="100%" height="484px">
      <v-card-title>
        <div class="card-title-container">
          <div class="card-inner-title-container">
            <h5>
              Which of the following best describes your role at {{ authData.companyName || 'your company' }}?
            </h5>
            <v-btn
              variant="flat"
              size="x-small"
              icon
              @click="updateModal(false)"
            >
              <v-icon
                color="iconDefault"
                size="18px"
                icon="$close"
              />
            </v-btn>
          </div>
          <p class="mt-2 description">
            This helps us tailor your JobNav experience.<br>
            You can change this at any time in your account settings.
          </p>
        </div>
      </v-card-title>
      <v-card-text>
        <v-radio-group v-model="selectedPersona" color="brandPrimary">
          <v-radio
            v-for="(value, key) in personaOptions"
            :key="key"
            :label="value"
            :value="key"
          />
        </v-radio-group>
      </v-card-text>
      <v-card-actions>
        <div class="errors">
          <span v-if="isError">There was an error updating your role</span>
        </div>
        <v-btn
          color="brandPrimary"
          variant="flat"
          rounded="xl"
          :disabled="!selectedPersona || saving"
          :loading="saving"
          @click="submit"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useModals } from '@/stores/modals/modals';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { personaOptions } from '@/components/settings/account/persona';
import { ref } from 'vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { updatePersona } from '@/api/settings';

const { show } = storeToRefs(useModals());
const { authData } = storeToRefs(useAppAuthData());
const { setModal } = useModals();

const selectedPersona = ref(authData.value.personaKey);
const saving = ref(false);
const isError = ref(false);

const updateModal = (value) => {
  useModals().setModal({ field: 'persona', value });

  if (!value) window.sessionStorage.setItem('dismissedPersonaModal', 'true');
};

const submit = async () => {
  try {
    saving.value = true;

    await updatePersona(selectedPersona.value);

    useAppAuthData().updatePersona(selectedPersona.value);

    useAppSnackbar().addSuccessSnackbar({ text: 'Your account has been updated' });

    setModal({ field: 'persona', value: false });
  } catch (error) {
    isError.value = true;
  } finally {
    saving.value = false;
  }
};
</script>

<style lang="less">
.persona-modal .v-card {
  .v-card-title {
    padding: 0;
  }

  .v-card-text {
    padding: 0 24px !important;
    letter-spacing: normal;
  }

  .v-card-actions {
    padding: 0 24px 24px 24px;
    display: flex;
    justify-content: space-between;

    .v-btn__content {
      letter-spacing: normal !important;
      text-transform: none;
    }

    .errors span {
      color: @basic-red;
    }
  }

  .card-title-container {
    width: 100%;
    border-bottom: 1px solid @stroke-default;
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 24px 24px 16px;

    .card-inner-title-container {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      h5 {
        text-wrap: wrap;
      }
    }

    .description {
      font-size: 12px;
      line-height: 14px;
      font-weight: 300;
    }
  }
}

.v-selection-control-group {
  width: 100%;
  max-height: 300px;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;

  .v-selection-control {
    max-height: 40px;

    .v-label {
      font-size: 14px;
      opacity: 1;
      color: @text-darkest;
    }
  }

  .v-selection-control__input > .v-icon {
    opacity: 1;
  }
}
</style>
